import React from 'react';

/**
 * StarRatingCard
 *
 * Single stat card displaying both the 28-day and 7-day rolling average
 * star ratings side by side, styled in teal to match the chart series.
 *
 * Props:
 *   ma28  {number|null}  — 28-day average star rating
 *   ma7   {number|null}  — 7-day average star rating
 */
const StarRatingCard = ({ ma28, ma7 }) => {
  const fmt = (v) => (v != null ? v.toFixed(2) : '—');

  // Direction badge: compare 7d vs 28d
  const delta = ma28 != null && ma7 != null ? ma7 - ma28 : null;
  const badge =
    delta != null
      ? `${delta >= 0 ? '+' : ''}${delta.toFixed(2)} vs 28d MA`
      : null;
  const badgeColor = delta == null ? '#4ecdc4' : delta >= 0 ? '#4ecdc4' : '#e05454';

  return (
    <div
      className="rounded p-2 h-100"
      style={{
        backgroundColor: '#111008',
        border: '1px solid #1a3a38',
        minWidth: 0,
      }}
    >
      {/* Card label */}
      <div className="d-flex align-items-center mb-1" style={{ gap: 5 }}>
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: '#4ecdc4',
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontSize: '0.65rem',
            color: '#4ecdc4',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            opacity: 0.8,
          }}
        >
          Star Rating MA
        </span>
      </div>

      {/* Two values side by side */}
      <div className="d-flex align-items-flex-start" style={{ gap: 12 }}>
        {/* 28-Day */}
        <div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#2ab5ac', lineHeight: 1 }}>
            {fmt(ma28)}
            <span style={{ fontSize: '0.8rem', marginLeft: 2, opacity: 0.7 }}>★</span>
          </div>
          <div style={{ fontSize: '0.65rem', color: '#4ecdc4', opacity: 0.6, marginTop: 2 }}>
            28-Day MA
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            width: 1,
            alignSelf: 'stretch',
            backgroundColor: '#1a3a38',
            flexShrink: 0,
          }}
        />

        {/* 7-Day */}
        <div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#7eddd6', lineHeight: 1 }}>
            {fmt(ma7)}
            <span style={{ fontSize: '0.8rem', marginLeft: 2, opacity: 0.7 }}>★</span>
          </div>
          <div style={{ fontSize: '0.65rem', color: '#4ecdc4', opacity: 0.6, marginTop: 2 }}>
            7-Day MA
          </div>
        </div>
      </div>

      {/* Delta badge */}
      {badge && (
        <div className="mt-1">
          <span
            style={{
              display: 'inline-block',
              fontSize: '0.65rem',
              fontWeight: 600,
              padding: '1px 7px',
              borderRadius: 20,
              backgroundColor: `${badgeColor}22`,
              color: badgeColor,
              border: `1px solid ${badgeColor}44`,
            }}
          >
            {badge}
          </span>
        </div>
      )}
    </div>
  );
};

export default StarRatingCard;
